import { memo, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import debounce from 'lodash.debounce';
import 'react-quill/dist/quill.snow.css';
import cn from 'classnames';

import textAreaStyles from './Textarea.module.scss';
import './ReactQuillEditor.css';
import { IReactQuillDelta } from '../../../APIPackage/KnmModule/interfaces/IReactQuillDelta';
import { removeTrailingNewline } from '../../../functions/removeTrailingNewline';

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline'], // Стили текста
    [{ list: 'ordered' }, { list: 'bullet' }], // Списки
  ],
};

const formats = ['bold', 'italic', 'underline', 'list', 'bullet'];

export interface IReactQuillEditorProps {
  id?: string;
  name?: string;
  disabled?: boolean;
  className?: string;
  value?: IReactQuillDelta | string;
  onChange?: (e: IReactQuillDelta) => void;
  error?: string;
  handleCustomChange?: (value: string) => void;
  warning?: string;
}

function cleanOpsAttributes(opsData: IReactQuillDelta) {
  if (!opsData || !Array.isArray(opsData.ops)) {
    return opsData;
  }

  const cleanedOps = opsData.ops.map(op => {
    if (!op.attributes) {
      const { attributes, ...rest } = op;

      return rest;
    }

    // Создаем новый объект атрибутов без null значений
    const cleanedAttributes = {};
    let hasNonEmptyAttributes = false;

    // eslint-disable-next-line no-restricted-syntax, @typescript-eslint/naming-convention
    for (const [key, value] of Object.entries(op.attributes)) {
      if (value !== null) {
        // @ts-ignore
        cleanedAttributes[key] = value;
        hasNonEmptyAttributes = true;
      }
    }

    // Если есть хотя бы один не-null атрибут, добавляем cleanedAttributes
    // Если все атрибуты null, полностью удаляем attributes
    if (hasNonEmptyAttributes) {
      return {
        ...op,
        attributes: cleanedAttributes,
      };
    }
    // Создаем копию объекта без attributes
    const { attributes, ...rest } = op;

    return rest;
  });

  return {
    ...opsData,
    ops: cleanedOps,
  };
}

function areQuillDeltasEqual(delta1: IReactQuillDelta, delta2: IReactQuillDelta): boolean {
  // Проверяем, что оба объекта существуют
  if (!delta1 || !delta2) {
    return delta1 === delta2;
  }

  if (typeof delta1 === 'string' || typeof delta2 === 'string') {
    return false;
  }

  // Проверяем длину массива ops
  if (delta1.ops.length !== delta2.ops.length) {
    return false;
  }

  // Сравниваем каждый элемент массива ops
  for (let i = 0; i < delta1.ops.length; i += 1) {
    const op1 = delta1.ops[i];
    const op2 = delta2.ops[i];

    // Проверяем insert
    if (op1.insert !== op2.insert) {
      return false;
    }

    // Проверяем наличие attributes у обоих
    if (!!op1.attributes !== !!op2.attributes) {
      return false;
    }

    // Если attributes существуют у обоих, сравниваем их
    if (op1.attributes && op2.attributes) {
      // Сравниваем каждое свойство attributes
      if (op1.attributes.bold !== op2.attributes.bold) {
        return false;
      }
      if (op1.attributes.italic !== op2.attributes.italic) {
        return false;
      }
      if (op1.attributes.underline !== op2.attributes.underline) {
        return false;
      }

      // Проверяем, нет ли дополнительных свойств в attributes
      const keys1 = Object.keys(op1.attributes);
      const keys2 = Object.keys(op2.attributes);

      if (keys1.length !== keys2.length) {
        return false;
      }

      // Убеждаемся, что все ключи совпадают
      // eslint-disable-next-line no-restricted-syntax
      for (const key of keys1) {
        if (!keys2.includes(key)) {
          return false;
        }
      }
    }
  }

  return true;
}

export const ReactQuillEditor = memo(
  ({
    id,
    disabled,
    className,
    onChange,
    error,
    value,
    handleCustomChange,
    warning,
    ...rest
  }: IReactQuillEditorProps) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const quillRef = useRef<any>(null);
    const lastValue = useRef<IReactQuillDelta | null>(null);
    const initialized = useRef<boolean>(false);
    const initializedFirstUpdate = useRef<boolean>(false);

    const classNames = cn([
      className,
      {
        ReactQuillEditorDefault: true,
        ReactQuillEditorInvalid: Boolean(error),
        ReactQuillEditorDisabled: disabled,
      },
    ]);

    useEffect(() => {
      if (value && !lastValue.current && !initialized.current) {
        let preparedValue = value;

        if (typeof value === 'string') {
          preparedValue = { ops: [{ insert: value }] };
        }
        lastValue.current = preparedValue as IReactQuillDelta;
        initialized.current = true;
        initializedFirstUpdate.current = true;
        quillRef.current?.getEditor().updateContents(preparedValue as IReactQuillDelta);
      }
    }, [value]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChange = (_: any, __: any, source: any, editor: any) => {
      if (!initializedFirstUpdate.current) {
        const content = removeTrailingNewline(editor.getContents()) as IReactQuillDelta;

        if (!areQuillDeltasEqual(cleanOpsAttributes(value as IReactQuillDelta), content)) {
          initialized.current = true;
          const textContent = content?.ops?.map(el => el.insert).join('');

          handleCustomChange?.(textContent);
          onChange?.(content as IReactQuillDelta);
        }
      } else {
        initializedFirstUpdate.current = false;
      }
    };

    const debouncedHandleChange = debounce(handleChange, 200);

    return (
      <div className={classNames}>
        <ReactQuill
          ref={quillRef}
          id={id}
          theme="snow"
          onChange={debouncedHandleChange}
          modules={modules}
          formats={formats}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          // value={value as any}
          readOnly={disabled}
          {...rest}
        />
        {warning && <div className={textAreaStyles.Warning}>{warning}</div>}
        {error && <div className={textAreaStyles.TextareaError}>{error}</div>}
      </div>
    );
  },
);
